/**
 * Wave 26 — 10 New City Agency Pages
 * Nantes 🇫🇷, Dortmund 🇩🇪, Łódź 🇵🇱, Bratislava 🇸🇰, Tallinn 🇪🇪,
 * Chandigarh 🇮🇳, Medellín 🇨🇴, Douala 🇨🇲, Dakar 🇸🇳, Bahrain Hub2 🇧🇭
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools with modern stack.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, and managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems with Figma-first approach.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance consulting.' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools with enterprise-grade security.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations, HIPAA-compliant apps.' },
  { name: 'SaaS & Technology', description: 'Product development and modernization for SaaS companies seed to Series C+.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, tenant portals.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE26: Record<string, CityAgencyData> = {

  nantes: {
    slug: 'software-development-agency-nantes',
    cityName: 'Nantes',
    countryName: 'France',
    flag: '🇫🇷',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Nantes | Call IT Dev',
      fr: 'Agence de Développement Informatique Nantes | Call IT Dev',
      es: 'Agencia Desarrollo Software Nantes | Call IT Dev',
      de: 'Softwareentwicklung Agentur Nantes | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Nantes. Custom SaaS, web, mobile, AI. French-speaking engineers, CET timezone, from €25/hr. 60% savings.',
      fr: 'Agence de développement informatique pour Nantes. SaaS, web, mobile, IA. Ingénieurs francophones, fuseau CET, dès 25 €/h. 60% d\'économies.',
      es: 'Agencia de desarrollo de software para Nantes. SaaS, web, móvil, IA. Ingenieros francófonos, desde 25 €/h.',
      de: 'Softwareentwicklungsagentur für Nantes. SaaS, Web, Mobile, KI. Französischsprachige Ingenieure, ab 25 €/h.',
    },
    keywords: ['software development agency Nantes', 'agence développement informatique Nantes', 'nearshore development Nantes', 'IT outsourcing Nantes'],
    heroTitle: {
      en: 'Software Development Agency for Nantes',
      fr: 'Agence de Développement Informatique pour Nantes',
      es: 'Agencia de Desarrollo de Software para Nantes',
      de: 'Softwareentwicklung Agentur für Nantes',
    },
    heroSubtitle: {
      en: 'French-speaking engineers at nearshore rates. CET timezone, 60% savings vs local Nantes agencies. Operational in 2 weeks.',
      fr: 'Ingénieurs francophones à tarifs nearshore. Fuseau CET, 60% d\'économies vs agences nantaises. Opérationnel en 2 semaines.',
      es: 'Ingenieros francófonos a precios nearshore. Zona CET, 60% ahorro vs agencias de Nantes.',
      de: 'Französischsprachige Ingenieure zu Nearshore-Preisen. CET-Zeitzone, 60% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Nantes Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Nantaises Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de Nantes Eligen Call IT Dev',
      de: 'Warum Nantaiser Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Native French Engineers', description: 'All developers speak fluent French. Seamless communication for Nantes-based teams.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same timezone as Nantes. Real-time collaboration with no jet lag.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at €25-40/hr vs €70-100/hr locally. Same quality, fraction of the cost.' },
      { icon: '✈️', title: 'Easy Access', description: 'Direct flights Nantes-Casablanca. Easy for onsite visits and team integration.' },
      { icon: '🏗️', title: 'Tech Hub Expertise', description: 'Supporting Nantes\' growing digital ecosystem with scalable engineering teams.' },
      { icon: '🚀', title: 'Fast Team Scaling', description: 'Skip the 3-month local recruitment cycle. Get a team in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Nantes?', answer: 'Nantes has a thriving tech scene but limited senior talent pool. Our nearshore model provides French-speaking engineers at 60% savings with CET timezone alignment.' },
      { question: 'What technologies do you use?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP, Docker, Kubernetes, TensorFlow.' },
      { question: 'How fast can you start?', answer: 'Initial team onboarding in 2 weeks. CVs and technical interviews within 5 business days.' },
      { question: 'Do you comply with RGPD?', answer: 'Yes. Enterprise-grade security, RGPD/GDPR compliant. DPA signed. All data handling follows CNIL guidelines.' },
      { question: 'Can you integrate with our Nantes team?', answer: 'Absolutely. Our developers embed into your Jira, Slack, Git workflow seamlessly.' },
      { question: 'What engagement models do you offer?', answer: 'Time & materials, fixed-price, and dedicated team models available.' },
    ],
    localRate: '€70-100',
    ourRate: '€25-40',
    savings: '60%+',
  },

  dortmund: {
    slug: 'software-development-agency-dortmund',
    cityName: 'Dortmund',
    countryName: 'Germany',
    flag: '🇩🇪',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Dortmund | Call IT Dev',
      fr: 'Agence de Développement Informatique Dortmund | Call IT Dev',
      de: 'Softwareentwicklung Agentur Dortmund | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Dortmund. Custom SaaS, web, mobile, AI. German-speaking engineers, CET timezone, from €30/hr. 55% savings.',
      fr: 'Agence de développement informatique pour Dortmund. SaaS, web, mobile, IA. Dès 30 €/h. 55% d\'économies.',
      de: 'Softwareentwicklungsagentur für Dortmund. SaaS, Web, Mobile, KI. Deutschsprachige Ingenieure, ab 30 €/h. 55% Ersparnis.',
    },
    keywords: ['software development agency Dortmund', 'Softwareentwicklung Dortmund', 'IT outsourcing Dortmund', 'nearshore development Dortmund'],
    heroTitle: {
      en: 'Software Development Agency for Dortmund',
      fr: 'Agence de Développement Informatique pour Dortmund',
      de: 'Softwareentwicklung Agentur für Dortmund',
    },
    heroSubtitle: {
      en: 'German-speaking engineers at nearshore rates. CET timezone, 55% savings vs Dortmund agencies.',
      fr: 'Ingénieurs germanophones à tarifs nearshore. Fuseau CET, 55% d\'économies.',
      de: 'Deutschsprachige Ingenieure zu Nearshore-Preisen. CET-Zeitzone, 55% Ersparnis vs lokale Agenturen.',
    },
    whyTitle: {
      en: 'Why Dortmund Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Dortmund Choisissent Call IT Dev',
      de: 'Warum Dortmunder Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🗣️', title: 'German-Speaking Engineers', description: 'Developers fluent in German for seamless communication with Ruhr-area teams.' },
      { icon: '⏰', title: 'Same CET Timezone', description: 'Real-time collaboration during Dortmund business hours.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €30-45/hr vs €80-120/hr in NRW region.' },
      { icon: '🏭', title: 'Industry 4.0 Expertise', description: 'Supporting Dortmund\'s industrial digital transformation with IoT and automation.' },
      { icon: '🛡️', title: 'DSGVO Compliant', description: 'Full German data protection compliance. Enterprise-grade security.' },
      { icon: '🚀', title: 'Rapid Scaling', description: 'Skip local talent shortages. Dedicated team operational in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Dortmund?', answer: 'The Ruhr area faces significant IT talent shortages. Our nearshore model delivers German-speaking engineers at 55% savings with identical timezone.' },
      { question: 'Do your engineers speak German?', answer: 'Yes. Engineers assigned to German clients are fluent in German (B2-C1+). All communication in German or English.' },
      { question: 'How do you handle DSGVO?', answer: 'Enterprise-grade security, DSGVO/GDPR compliant. Auftragsverarbeitungsvertrag (AVV) signed.' },
      { question: 'What industries do you serve?', answer: 'FinTech, Industry 4.0, logistics, healthcare, e-commerce, and SaaS — all strong in the Dortmund/NRW region.' },
      { question: 'Can you visit our Dortmund office?', answer: 'Yes. Direct flights to Düsseldorf (30 min to Dortmund). We support quarterly onsite sprints.' },
      { question: 'Minimum team size?', answer: 'Start with 2 developers and scale to 30+. Flexible engagement models.' },
    ],
    localRate: '€80-120',
    ourRate: '€30-45',
    savings: '55%+',
  },

  lodz: {
    slug: 'software-development-agency-lodz',
    cityName: 'Łódź',
    countryName: 'Poland',
    flag: '🇵🇱',
    timezone: 'CET (GMT+1)',
    currency: 'PLN/EUR',
    title: {
      en: 'Software Development Agency Łódź | Call IT Dev',
      fr: 'Agence de Développement Informatique Łódź | Call IT Dev',
      de: 'Softwareentwicklung Agentur Łódź | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Łódź. Custom SaaS, web, mobile, AI. CET timezone, from €25/hr. 50% savings vs local rates.',
      fr: 'Agence de développement informatique pour Łódź. SaaS, web, mobile, IA. Dès 25 €/h. 50% d\'économies.',
      de: 'Softwareentwicklungsagentur für Łódź. SaaS, Web, Mobile, KI. Ab 25 €/h. 50% Ersparnis.',
    },
    keywords: ['software development agency Łódź', 'IT outsourcing Łódź', 'nearshore development Poland Łódź'],
    heroTitle: {
      en: 'Software Development Agency for Łódź',
      fr: 'Agence de Développement Informatique pour Łódź',
      de: 'Softwareentwicklung Agentur für Łódź',
    },
    heroSubtitle: {
      en: 'Skilled engineers at nearshore rates. CET timezone, 50% savings vs Łódź agencies. Fast team scaling.',
      fr: 'Ingénieurs qualifiés à tarifs nearshore. Fuseau CET, 50% d\'économies vs agences de Łódź.',
      de: 'Qualifizierte Ingenieure zu Nearshore-Preisen. CET-Zeitzone, 50% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Łódź Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Łódź Choisissent Call IT Dev',
      de: 'Warum Łódźer Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🎓', title: 'Engineering Talent', description: 'Access top-tier engineering talent without Łódź local market constraints.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Identical timezone for seamless real-time collaboration.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Senior devs at €25-35/hr vs €45-70/hr locally in Łódź.' },
      { icon: '🌍', title: 'Multilingual Teams', description: 'Engineers fluent in English, French, and Arabic for global projects.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'Enterprise-grade security with full EU data protection compliance.' },
      { icon: '🚀', title: 'Scale Fast', description: 'Dedicated team operational in 2 weeks. No local recruitment hassle.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Łódź?', answer: 'Łódź is a growing tech hub but faces talent competition. Our nearshore model provides skilled engineers at 50% savings.' },
      { question: 'What timezone alignment?', answer: 'Same CET timezone as Łódź. Full overlap for daily standups and collaboration.' },
      { question: 'How fast can you start?', answer: '2-week onboarding. CVs within 5 business days.' },
      { question: 'Do you comply with RODO/GDPR?', answer: 'Yes. Enterprise-grade security, GDPR/RODO compliant.' },
      { question: 'What engagement models?', answer: 'Time & materials, fixed-price, and dedicated team models.' },
      { question: 'Can you scale quickly?', answer: 'Start with 2 developers, scale to 30+. Flexible arrangements.' },
    ],
    localRate: '€45-70',
    ourRate: '€25-35',
    savings: '50%+',
  },

  bratislava: {
    slug: 'software-development-agency-bratislava',
    cityName: 'Bratislava',
    countryName: 'Slovakia',
    flag: '🇸🇰',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Bratislava | Call IT Dev',
      fr: 'Agence de Développement Informatique Bratislava | Call IT Dev',
      de: 'Softwareentwicklung Agentur Bratislava | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Bratislava. Custom SaaS, web, mobile, AI. CET timezone, from €25/hr. 45% savings vs local rates.',
      fr: 'Agence de développement informatique pour Bratislava. SaaS, web, mobile, IA. Dès 25 €/h.',
      de: 'Softwareentwicklungsagentur für Bratislava. SaaS, Web, Mobile, KI. Ab 25 €/h. 45% Ersparnis.',
    },
    keywords: ['software development agency Bratislava', 'IT outsourcing Bratislava', 'nearshore development Slovakia'],
    heroTitle: {
      en: 'Software Development Agency for Bratislava',
      fr: 'Agence de Développement Informatique pour Bratislava',
      de: 'Softwareentwicklung Agentur für Bratislava',
    },
    heroSubtitle: {
      en: 'Expert engineers at nearshore rates. CET timezone, 45% savings vs Bratislava agencies.',
      fr: 'Ingénieurs experts à tarifs nearshore. Fuseau CET, 45% d\'économies.',
      de: 'Experten-Ingenieure zu Nearshore-Preisen. CET-Zeitzone, 45% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Bratislava Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Bratislava Choisissent Call IT Dev',
      de: 'Warum Bratislavaer Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🎓', title: 'Top Talent Access', description: 'Access 200+ engineers without competing in Bratislava\'s tight IT market.' },
      { icon: '⏰', title: 'Same CET Timezone', description: 'Identical working hours for seamless collaboration.' },
      { icon: '💰', title: '45% Cost Savings', description: 'Senior devs at €25-38/hr vs €50-75/hr in Bratislava.' },
      { icon: '🌍', title: 'EU-Compatible', description: 'GDPR compliant, EU data protection standards.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'Enterprise-grade security and compliance.' },
      { icon: '🚀', title: 'Quick Start', description: 'Dedicated team in 2 weeks. No long recruitment cycles.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Bratislava?', answer: 'Bratislava\'s small market creates talent scarcity. Our nearshore model provides skilled engineers at 45% savings with CET alignment.' },
      { question: 'What timezone?', answer: 'Same CET timezone. Full overlap for real-time collaboration.' },
      { question: 'GDPR compliant?', answer: 'Yes. Enterprise-grade security, fully GDPR compliant.' },
      { question: 'How fast to start?', answer: '2-week onboarding with CVs in 5 business days.' },
      { question: 'What technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP, Docker, Kubernetes.' },
      { question: 'Engagement models?', answer: 'Time & materials, fixed-price, and dedicated team models.' },
    ],
    localRate: '€50-75',
    ourRate: '€25-38',
    savings: '45%+',
  },

  tallinn_hub: {
    slug: 'software-development-agency-tallinn-hub',
    cityName: 'Tallinn',
    countryName: 'Estonia',
    flag: '🇪🇪',
    timezone: 'EET (GMT+2)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Tallinn | Call IT Dev',
      fr: 'Agence de Développement Informatique Tallinn | Call IT Dev',
      de: 'Softwareentwicklung Agentur Tallinn | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Tallinn. Custom SaaS, web, mobile, AI. EET timezone, from €25/hr. 45% savings. e-Estonia digital expertise.',
      fr: 'Agence de développement pour Tallinn. SaaS, web, mobile, IA. Dès 25 €/h. 45% d\'économies.',
      de: 'Softwareentwicklungsagentur für Tallinn. SaaS, Web, Mobile, KI. Ab 25 €/h. 45% Ersparnis.',
    },
    keywords: ['software development agency Tallinn', 'IT outsourcing Tallinn', 'nearshore development Estonia'],
    heroTitle: {
      en: 'Software Development Agency for Tallinn',
      fr: 'Agence de Développement Informatique pour Tallinn',
      de: 'Softwareentwicklung Agentur für Tallinn',
    },
    heroSubtitle: {
      en: 'Expert engineers for Estonia\'s digital economy. EET timezone, 45% savings vs Tallinn agencies.',
      fr: 'Ingénieurs experts pour l\'économie numérique estonienne. EET, 45% d\'économies.',
      de: 'Experten-Ingenieure für Estlands Digitalwirtschaft. EET-Zeitzone, 45% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Tallinn Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Tallinn Choisissent Call IT Dev',
      de: 'Warum Tallinner Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '💻', title: 'e-Estonia Expertise', description: 'Supporting Tallinn startups and digital government projects.' },
      { icon: '⏰', title: 'EET Timezone', description: '1-hour difference. Strong overlap for daily collaboration.' },
      { icon: '💰', title: '45% Cost Savings', description: 'Senior devs at €25-38/hr vs €55-80/hr in Tallinn.' },
      { icon: '🌍', title: 'EU Standards', description: 'GDPR compliant, enterprise-grade security.' },
      { icon: '🚀', title: 'Startup-Friendly', description: 'Flexible engagement from 2 devs. Scale as you grow.' },
      { icon: '🛡️', title: 'Security First', description: 'Enterprise-grade security for fintech and govtech projects.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Tallinn?', answer: 'Tallinn is Europe\'s digital capital but has a small talent pool. Our nearshore model extends your team at 45% savings.' },
      { question: 'Can you work with Estonian startups?', answer: 'Yes! We work with seed-stage to Series B startups. Flexible engagement, no long-term commitments.' },
      { question: 'GDPR compliant?', answer: 'Yes. Enterprise-grade security, fully GDPR compliant, DPA signed.' },
      { question: 'How fast to start?', answer: '2-week onboarding. CVs within 5 business days.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Go, AWS, Azure, Docker, Kubernetes.' },
      { question: 'Engagement models?', answer: 'Time & materials, fixed-price, and dedicated team models.' },
    ],
    localRate: '€55-80',
    ourRate: '€25-38',
    savings: '45%+',
  },

  chandigarh: {
    slug: 'software-development-agency-chandigarh',
    cityName: 'Chandigarh',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (GMT+5:30)',
    currency: 'INR/USD',
    title: {
      en: 'Software Development Agency Chandigarh | Call IT Dev',
      fr: 'Agence de Développement Informatique Chandigarh | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Chandigarh. Custom SaaS, web, mobile, AI. From $20/hr. 70% savings vs US/UK rates.',
      fr: 'Agence de développement pour Chandigarh. SaaS, web, mobile, IA. Dès 20 $/h. 70% d\'économies.',
    },
    keywords: ['software development agency Chandigarh', 'IT outsourcing Chandigarh', 'software company Chandigarh'],
    heroTitle: {
      en: 'Software Development Agency for Chandigarh',
      fr: 'Agence de Développement Informatique pour Chandigarh',
    },
    heroSubtitle: {
      en: 'Expert engineers for Chandigarh\'s growing tech ecosystem. 70% savings vs US/UK rates.',
      fr: 'Ingénieurs experts pour l\'écosystème tech de Chandigarh. 70% d\'économies.',
    },
    whyTitle: {
      en: 'Why Chandigarh Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Chandigarh Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🎓', title: 'IT Hub Talent', description: 'Supporting Chandigarh\'s IT Park ecosystem with scalable teams.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Competitive rates vs US/UK agencies. Premium quality.' },
      { icon: '🌍', title: 'Global Standards', description: 'Enterprise-grade security. International quality processes.' },
      { icon: '⏰', title: 'Flexible Hours', description: 'Overlap hours for daily collaboration and standups.' },
      { icon: '🚀', title: 'Fast Scaling', description: 'Dedicated team in 2 weeks. Scale to 50+ engineers.' },
      { icon: '🛡️', title: 'Data Security', description: 'Enterprise-grade security and compliance standards.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Chandigarh?', answer: 'Chandigarh IT Park is growing but faces talent competition from Bangalore/Hyderabad. Our nearshore model provides skilled engineers at competitive rates.' },
      { question: 'What technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP, Docker, Kubernetes, TensorFlow.' },
      { question: 'How fast to start?', answer: '2-week onboarding. CVs within 5 business days.' },
      { question: 'Data security?', answer: 'Enterprise-grade security, GDPR compliant.' },
      { question: 'Engagement models?', answer: 'Time & materials, fixed-price, dedicated team models.' },
      { question: 'Can you scale?', answer: 'Start with 2 devs, scale to 50+. No long-term commitments.' },
    ],
    localRate: '$40-80',
    ourRate: '$20-35',
    savings: '70%+',
  },

  medellin: {
    slug: 'software-development-agency-medellin',
    cityName: 'Medellín',
    countryName: 'Colombia',
    flag: '🇨🇴',
    timezone: 'COT (GMT-5)',
    currency: 'COP/USD',
    title: {
      en: 'Software Development Agency Medellín | Call IT Dev',
      fr: 'Agence de Développement Informatique Medellín | Call IT Dev',
      es: 'Agencia Desarrollo Software Medellín | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Medellín. Custom SaaS, web, mobile, AI. From $22/hr. 65% savings vs US rates.',
      fr: 'Agence de développement pour Medellín. SaaS, web, mobile, IA. Dès 22 $/h. 65% d\'économies.',
      es: 'Agencia de desarrollo de software para Medellín. SaaS, web, móvil, IA. Desde 22 $/h. 65% ahorro.',
    },
    keywords: ['software development agency Medellín', 'agencia desarrollo software Medellín', 'IT outsourcing Medellín', 'nearshore development Colombia'],
    heroTitle: {
      en: 'Software Development Agency for Medellín',
      fr: 'Agence de Développement Informatique pour Medellín',
      es: 'Agencia de Desarrollo de Software para Medellín',
    },
    heroSubtitle: {
      en: 'Bilingual engineers serving Medellín\'s innovation hub. US timezone overlap, 65% savings.',
      fr: 'Ingénieurs bilingues pour le hub d\'innovation de Medellín. 65% d\'économies.',
      es: 'Ingenieros bilingües para el hub de innovación de Medellín. 65% ahorro.',
    },
    whyTitle: {
      en: 'Why Medellín Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Medellín Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de Medellín Eligen Call IT Dev',
    },
    whyReasons: [
      { icon: '🏙️', title: 'Innovation City', description: 'Supporting Medellín\'s tech transformation with scalable engineering teams.' },
      { icon: '⏰', title: 'US Timezone Overlap', description: 'COT timezone provides strong overlap with US East Coast.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Senior devs at $22-38/hr vs $80-150/hr in the US.' },
      { icon: '🗣️', title: 'Bilingual Teams', description: 'Engineers fluent in Spanish and English.' },
      { icon: '🛡️', title: 'Data Security', description: 'Enterprise-grade security, GDPR compliant.' },
      { icon: '🚀', title: 'Fast Start', description: 'Dedicated team in 2 weeks. Scale as needed.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Medellín?', answer: 'Medellín is Latin America\'s Silicon Valley but faces growing talent competition. Our model extends your team at 65% savings.' },
      { question: 'What languages?', answer: 'Engineers fluent in Spanish and English. French-speaking teams also available.' },
      { question: 'US timezone?', answer: 'COT (GMT-5) provides 6+ hours of overlap with US East Coast.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, Docker, Kubernetes.' },
      { question: 'How fast?', answer: '2-week onboarding. CVs in 5 business days.' },
      { question: 'Data protection?', answer: 'Enterprise-grade security, GDPR/Ley 1581 compliant.' },
    ],
    localRate: '$40-70',
    ourRate: '$22-38',
    savings: '65%+',
  },

  douala: {
    slug: 'software-development-agency-douala',
    cityName: 'Douala',
    countryName: 'Cameroon',
    flag: '🇨🇲',
    timezone: 'WAT (GMT+1)',
    currency: 'XAF/EUR',
    title: {
      en: 'Software Development Agency Douala | Call IT Dev',
      fr: 'Agence de Développement Informatique Douala | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Douala, Cameroon. Custom SaaS, web, mobile, AI. Bilingual FR/EN engineers, WAT timezone, from $18/hr.',
      fr: 'Agence de développement informatique pour Douala. SaaS, web, mobile, IA. Ingénieurs bilingues FR/EN, fuseau WAT, dès 18 $/h.',
    },
    keywords: ['software development agency Douala', 'agence développement informatique Douala', 'IT outsourcing Cameroon'],
    heroTitle: {
      en: 'Software Development Agency for Douala',
      fr: 'Agence de Développement Informatique pour Douala',
    },
    heroSubtitle: {
      en: 'Bilingual French/English engineers for Cameroon\'s economic capital. WAT timezone, 80% savings.',
      fr: 'Ingénieurs bilingues français/anglais pour la capitale économique du Cameroun. Fuseau WAT, 80% d\'économies.',
    },
    whyTitle: {
      en: 'Why Douala Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Douala Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'Central Africa Hub', description: 'Supporting Douala\'s growing digital ecosystem and CEMAC region.' },
      { icon: '🗣️', title: 'Bilingual FR/EN', description: 'Engineers fluent in French and English for Cameroon\'s bilingual market.' },
      { icon: '⏰', title: 'WAT Timezone', description: 'Same timezone as CET. Real-time collaboration with Europe.' },
      { icon: '💰', title: '80% Cost Savings', description: 'Senior devs at $18-30/hr vs $80-120/hr in Europe.' },
      { icon: '🏗️', title: 'Scalable Teams', description: 'Scale from 2 to 30+ developers as your project grows.' },
      { icon: '🛡️', title: 'Data Security', description: 'Enterprise-grade security, GDPR compliant.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Douala?', answer: 'Douala is Cameroon\'s economic capital with a growing tech scene. Our model provides bilingual engineers at 80% savings.' },
      { question: 'Languages?', answer: 'Engineers fluent in French and English — matching Cameroon\'s bilingual market.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, Docker, Kubernetes.' },
      { question: 'How fast?', answer: '2-week onboarding. CVs within 5 business days.' },
      { question: 'Data protection?', answer: 'Enterprise-grade security, GDPR compliant.' },
      { question: 'Engagement models?', answer: 'Time & materials, fixed-price, and dedicated team models.' },
    ],
    localRate: '$50-90',
    ourRate: '$18-30',
    savings: '80%+',
  },

  dakar: {
    slug: 'software-development-agency-dakar',
    cityName: 'Dakar',
    countryName: 'Senegal',
    flag: '🇸🇳',
    timezone: 'GMT (GMT+0)',
    currency: 'XOF/EUR',
    title: {
      en: 'Software Development Agency Dakar | Call IT Dev',
      fr: 'Agence de Développement Informatique Dakar | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Dakar, Senegal. Custom SaaS, web, mobile, AI. French-speaking engineers, GMT timezone, from $18/hr.',
      fr: 'Agence de développement informatique pour Dakar. SaaS, web, mobile, IA. Ingénieurs francophones, fuseau GMT, dès 18 $/h. 80% d\'économies.',
    },
    keywords: ['software development agency Dakar', 'agence développement informatique Dakar', 'IT outsourcing Senegal'],
    heroTitle: {
      en: 'Software Development Agency for Dakar',
      fr: 'Agence de Développement Informatique pour Dakar',
    },
    heroSubtitle: {
      en: 'French-speaking engineers for West Africa\'s tech hub. GMT timezone, 80% savings.',
      fr: 'Ingénieurs francophones pour le hub tech d\'Afrique de l\'Ouest. Fuseau GMT, 80% d\'économies.',
    },
    whyTitle: {
      en: 'Why Dakar Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Dakar Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'West Africa Hub', description: 'Supporting Dakar\'s Position as West Africa\'s leading tech capital.' },
      { icon: '🗣️', title: 'French-Speaking', description: 'All engineers are native French speakers for seamless collaboration.' },
      { icon: '⏰', title: 'GMT Timezone', description: 'Perfect overlap with UK and Western Africa business hours.' },
      { icon: '💰', title: '80% Cost Savings', description: 'Senior devs at $18-30/hr vs $80-120/hr in Europe.' },
      { icon: '🏗️', title: 'Digital Africa', description: 'Supporting Senegal\'s Stratégie Sénégal Numérique 2025.' },
      { icon: '🛡️', title: 'Compliance', description: 'Enterprise-grade security, GDPR compliant.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Dakar?', answer: 'Dakar is emerging as West Africa\'s tech capital. Our model provides French-speaking engineers at 80% savings with GMT timezone.' },
      { question: 'Languages?', answer: 'Engineers fluent in French (native) and English. Arabic speakers also available.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, Docker, Kubernetes.' },
      { question: 'How fast?', answer: '2-week onboarding. CVs within 5 business days.' },
      { question: 'Data protection?', answer: 'Enterprise-grade security, GDPR compliant, aligned with Senegal\'s data protection law.' },
      { question: 'Engagement models?', answer: 'Time & materials, fixed-price, and dedicated team models.' },
    ],
    localRate: '$50-90',
    ourRate: '$18-30',
    savings: '80%+',
  },

  bahrain_hub2: {
    slug: 'software-development-agency-bahrain-hub2',
    cityName: 'Bahrain',
    countryName: 'Bahrain',
    flag: '🇧🇭',
    timezone: 'AST (GMT+3)',
    currency: 'BHD/USD',
    title: {
      en: 'Software Development Agency Bahrain | Call IT Dev',
      fr: 'Agence de Développement Informatique Bahreïn | Call IT Dev',
      ar: 'وكالة تطوير برمجيات البحرين | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Bahrain. Custom SaaS, web, mobile, AI, FinTech. Arabic/English-speaking engineers, from $25/hr.',
      fr: 'Agence de développement pour le Bahreïn. SaaS, web, mobile, IA, FinTech. Dès 25 $/h.',
      ar: 'وكالة تطوير برمجيات للبحرين. SaaS، ويب، موبايل، ذكاء اصطناعي، فينتك. من 25 $/ساعة.',
    },
    keywords: ['software development agency Bahrain', 'IT outsourcing Bahrain', 'fintech development Bahrain'],
    heroTitle: {
      en: 'Software Development Agency for Bahrain',
      fr: 'Agence de Développement Informatique pour Bahreïn',
      ar: 'وكالة تطوير برمجيات للبحرين',
    },
    heroSubtitle: {
      en: 'Arabic/English engineers for Bahrain\'s FinTech hub. AST timezone, 65% savings.',
      fr: 'Ingénieurs arabe/anglais pour le hub FinTech du Bahreïn. 65% d\'économies.',
      ar: 'مهندسون عرب/إنجليز لمركز الفينتك البحريني. توفير 65%.',
    },
    whyTitle: {
      en: 'Why Bahrain Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises du Bahreïn Choisissent Call IT Dev',
      ar: 'لماذا تختار شركات البحرين Call IT Dev',
    },
    whyReasons: [
      { icon: '🏦', title: 'FinTech Expertise', description: 'Supporting Bahrain\'s position as MENA FinTech hub with specialized development.' },
      { icon: '🗣️', title: 'Arabic/English', description: 'Engineers fluent in Arabic and English for GCC market.' },
      { icon: '⏰', title: 'AST Timezone', description: '2-hour difference from Casablanca. Strong daily overlap.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Senior devs at $25-40/hr vs $80-150/hr in Bahrain.' },
      { icon: '🛡️', title: 'CBB Compliance', description: 'Experience with Central Bank of Bahrain regulatory requirements.' },
      { icon: '🚀', title: 'Scale Fast', description: 'Dedicated team in 2 weeks for fintech and enterprise projects.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why outsource from Bahrain?', answer: 'Bahrain is MENA\'s FinTech hub but has limited local talent. Our model provides Arabic/English engineers at 65% savings.' },
      { question: 'Languages?', answer: 'Engineers fluent in Arabic (native) and English. French also available.' },
      { question: 'FinTech experience?', answer: 'Yes. Payment platforms, trading systems, Islamic finance, CBB compliance.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, Docker, Kubernetes.' },
      { question: 'How fast?', answer: '2-week onboarding. CVs within 5 business days.' },
      { question: 'Data protection?', answer: 'Enterprise-grade security, GDPR and PDPL compliant.' },
    ],
    localRate: '$80-150',
    ourRate: '$25-40',
    savings: '65%+',
  },

};
